// Record-time capture of cross-origin stylesheet text.
//
// rrweb's `inlineStylesheet: true` inlines a stylesheet into the snapshot only
// when the document can read its cssRules. Cross-origin sheets loaded without
// a `crossorigin` attribute are CORS-opaque, so rrweb can only record their
// URL — and a URL is not a contract: by the time a replay renders, the origin
// may serve a different build than the one the user's browser applied (many
// CDNs ignore cache-buster params). Replaying record-day DOM with current-day
// CSS produces phantom visual defects the user never saw.
//
// This module closes that gap at the only place it can be closed: record
// time. For each stylesheet the snapshot could NOT inline, it fetches the CSS
// text (cache-first, so it prefers the exact bytes the user's browser already
// downloaded) and emits it as an `hb-css-snapshot` custom event. Replay-side
// consumers serve these bodies via network interception, rebuilding the page
// from record-time truth.
//
// Invariants (matching the error-capture subsystem): never throws, never
// blocks the recording path (all work is async), bounded output (per-sheet
// and per-session byte caps), and each href is captured at most once per
// session.

export const CSS_SNAPSHOT_TAG = 'hb-css-snapshot';

export interface CssSnapshotPayload {
    href: string;
    cssText: string;
}

// A sheet bigger than this is skipped: it would bloat the event stream, and
// pathological sizes usually indicate something other than a real stylesheet.
const MAX_SHEET_BYTES = 1_500_000;
// Total budget per session across all captured sheets.
const MAX_TOTAL_BYTES = 5_000_000;
// Re-scan delay after each trigger: stylesheets referenced by a fresh
// navigation may still be loading when the snapshot fires.
const RESCAN_DELAY_MS = 3_000;

type FetchLike = (input: string, init?: RequestInit) => Promise<{ ok: boolean; text(): Promise<string> }>;

// Hrefs of stylesheets in the document that rrweb could NOT have inlined:
// present in document.styleSheets but with CORS-opaque cssRules.
export function unreadableStylesheetHrefs(doc: Document): string[] {
    const out: string[] = [];
    let sheets: StyleSheetList;
    try {
        sheets = doc.styleSheets;
    } catch {
        return out;
    }
    for (let i = 0; i < sheets.length; i++) {
        const sheet = sheets[i] as CSSStyleSheet;
        let href: string | null = null;
        try {
            href = sheet.href;
        } catch {
            continue;
        }
        if (!href || href.startsWith('data:') || href.startsWith('blob:')) continue;
        try {
            // Readable rules mean rrweb inlined it already — nothing to do.
            void sheet.cssRules;
        } catch {
            out.push(href);
        }
    }
    return out;
}

export class CssSnapshotCapture {
    private captured = new Set<string>();
    private bytesSent = 0;
    private rescanTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private emit: (payload: CssSnapshotPayload) => void,
        private fetchImpl?: FetchLike,
    ) {}

    // Capture every currently-unreadable stylesheet, then schedule one
    // re-scan for late-loading sheets. Safe to call repeatedly (navigation
    // snapshots): already-captured hrefs are skipped.
    captureFromDocument(doc: Document): void {
        try {
            void this.capture(unreadableStylesheetHrefs(doc));
            if (this.rescanTimer) clearTimeout(this.rescanTimer);
            this.rescanTimer = setTimeout(() => {
                this.rescanTimer = null;
                try {
                    void this.capture(unreadableStylesheetHrefs(doc));
                } catch {
                    /* capture is best-effort */
                }
            }, RESCAN_DELAY_MS);
        } catch {
            /* never throw into the recording path */
        }
    }

    // Fetch + emit the given hrefs, deduped and byte-capped. Exposed for
    // tests; production entry is captureFromDocument.
    async capture(hrefs: string[]): Promise<void> {
        const fetcher: FetchLike | undefined =
            this.fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);
        if (!fetcher) return;

        for (const href of hrefs) {
            if (this.captured.has(href)) continue;
            if (this.bytesSent >= MAX_TOTAL_BYTES) return;
            // Mark before fetching: a failed fetch will not succeed on a
            // rescan seconds later, and retry loops are worse than a gap the
            // replay-side fidelity gate already handles.
            this.captured.add(href);
            try {
                // force-cache: prefer the bytes the user's browser already
                // downloaded for this page — the exact record-time build.
                const res = await fetcher(href, {
                    mode: 'cors',
                    credentials: 'omit',
                    cache: 'force-cache',
                });
                if (!res.ok) continue;
                const cssText = await res.text();
                if (!cssText || cssText.length > MAX_SHEET_BYTES) continue;
                if (this.bytesSent + cssText.length > MAX_TOTAL_BYTES) continue;
                this.bytesSent += cssText.length;
                this.emit({ href, cssText });
            } catch {
                // CORS-denied or network failure: cannot capture. The
                // replay-side fidelity gate covers this sheet instead.
            }
        }
    }

    dispose(): void {
        if (this.rescanTimer) clearTimeout(this.rescanTimer);
        this.rescanTimer = null;
    }
}
